import { allCommands, createGroup } from "app/client/components/commands";
import { GristDoc } from "app/client/components/GristDoc";
import { makeT } from "app/client/lib/localization";
import * as log from "app/client/lib/log";
import { inlineMarkdown } from "app/client/lib/markdown";
import { getStorage } from "app/client/lib/storage";
import { logTelemetryEvent } from "app/client/lib/telemetry";
import { ChatHistory } from "app/client/models/ChatHistory";
import { reportError } from "app/client/models/errors";
import { IAssistantPanel } from "app/client/ui/IAssistantPanel";
import {
  cssKeyboardShortcut,
  cssLinkText,
  cssPageButton,
  cssPageEntry,
  cssPageIcon,
  cssTools,
} from "app/client/ui/LeftPanelCommon";
import { hoverTooltip } from "app/client/ui/tooltips";
import { theme } from "app/client/ui2018/cssVars";
import { icon } from "app/client/ui2018/icons";
import { cssLink } from "app/client/ui2018/links";
import { menu, menuItem } from "app/client/ui2018/menus";
import { Assistant, cssAiImage, cssAvatar } from "app/client/widgets/Assistant";
import {
  cssAiIntroMessage,
  cssAiMessageParagraph,
} from "app/client/widgets/FormulaAssistant";
import { AssistantState } from "app/common/ActiveDocAPI";
import { AssistanceState, DeveloperPromptVersion } from "app/common/Assistance";
import { commonUrls } from "app/common/gristUrls";
import { safeJsonParse, useBindable } from "app/common/gutil";
import { TelemetryEvent, TelemetryMetadata } from "app/common/Telemetry";
import { getGristConfig } from "app/common/urlUtils";

import {
  BindableValue,
  Computed,
  Disposable,
  dom,
  DomContents,
  DomElementArg,
  makeTestId,
  Observable,
  styled,
} from "grainjs";
import { v4 as uuidv4 } from "uuid";

// eslint-disable-next-line local/makeT-filename
const t = makeT("Assistant");

const testId = makeTestId("test-assistant-");

const ASSISTANT_TOOLTIP_KEY = "assistantToolbarTooltip";

export function buildAssistantPanel(gristDoc: GristDoc): IAssistantPanel | null {
  const { assistant } = getGristConfig();
  if (!assistant || assistant.version === 1) {
    return null;
  }

  return AssistantPanel.create(gristDoc, gristDoc);
}

class AssistantPanel extends Disposable implements IAssistantPanel {
  public readonly isOpen = Computed.create(this, use => use(this._gristDoc.docPageModel.innerLeftPanelOpen));

  private _appModel = this._gristDoc.appModel;
  private _userId = this._appModel.currentUser?.id ?? 0;
  private _docId = Observable.create(this, this._gristDoc.docId() ?? "");
  private _conversationId = uuidv4();

  private _history = jsonStorageComputed<ChatHistory>({
    owner: this,
    storage: getStorage(),
    keyFn: use => this._key(use(this._docId), "assistantHistory"),
    defaultValue: () => ({
      messages: [],
      conversationId: this._conversationId,
      developerPromptVersion: "default",
    }),
  });

  private _assistant: Assistant = Assistant.create(this, {
    history: this._history,
    gristDoc: this._gristDoc,
    onSend: this._sendMessage.bind(this),
    onEscape: () => this._closeAndFocusSection(),
    buildIntroMessage,
  });

  private _composerCommands = this.autoDispose(createGroup({
    toggleAssistant: () => this.toggle(),
  }, this, false));

  private _headerElem: HTMLElement | undefined;
  private _panelElem: HTMLElement | undefined;

  constructor(private _gristDoc: GristDoc) {
    super();

    this.autoDispose(this._gristDoc.docPageModel.currentDocId.addListener((newDocId) => {
      if (!newDocId || newDocId === this._docId.get()) { return; }
      this._history.copyTo(this._key(newDocId, "assistantHistory"));
      this._docId.set(newDocId);
    }));
  }

  public open() {
    allCommands.innerLeftPanelOpen.run();
    this._assistant.focus();
    this._logTelemetryEvent("assistantOpen");
  }

  public close() {
    allCommands.innerLeftPanelClose.run();
  }

  public toggle() {
    if (!this.isOpen.get()) {
      this.open();
    } else if (this._hasFocus()) {
      this._closeAndFocusSection();
    } else {
      this._assistant.focus();
    }
  }

  public ask(prompt: string, developerPromptVersion?: DeveloperPromptVersion) {
    this.open();
    if (developerPromptVersion) {
      this._setDeveloperPromptVersion(developerPromptVersion);
    }
    this._assistant.send(prompt).catch(reportError);
  }

  public setState({ prompt, developerPromptVersion = "new-document" }: AssistantState) {
    this._setDeveloperPromptVersion(developerPromptVersion);
    this._assistant.send(prompt).catch(reportError);
  }

  public buildDom(): DomContents {
    return [
      this._buildHeader(),
      this._buildBody(),
    ];
  }

  private _buildHeader() {
    return cssPanelHeader(
      (elem: HTMLElement) => { this._headerElem = elem; },
      cssPanelHeaderIcon("Sparkle"),
      cssPanelHeaderTitle(
        cssTitleBox(
          cssTitleLabel(t("Assistant")),
          testId("panel-title"),
        ),
      ),
      cssPanelToolbar(
        cssToolbarButton(
          icon("Dots"),
          menu(() => [
            menuItem(
              () => this._assistant.clear(),
              t("Clear Conversation"),
              testId("options-clear-conversation"),
            ),
          ]),
          testId("options"),
        ),
        cssToolbarButton(
          icon("CrossBig"),
          dom.on("click", () => this.close()),
          hoverTooltip(t("Close"), { key: ASSISTANT_TOOLTIP_KEY }),
          testId("panel-close"),
        ),
      ),
      testId("panel-header"),
    );
  }

  private _buildBody() {
    return cssAssistantPanel(
      testId("panel"),
      (elem: HTMLElement) => { this._panelElem = elem; },
      dom.onKeyDown({
        Escape: (ev) => {
          ev.stopPropagation();
          this._closeAndFocusSection();
        },
      }),
      this._assistant.buildDom(),
      (elem: HTMLElement) => {
        const input = elem.querySelector("textarea");
        if (input) { this._composerCommands.attach(input); }
      },
    );
  }

  private _closeAndFocusSection() {
    this.close();
    this._gristDoc.regionFocusSwitcher?.focusActiveSection();
  }

  private _hasFocus() {
    const active = document.activeElement;
    return Boolean(this._headerElem?.contains(active) || this._panelElem?.contains(active));
  }

  private _key(docId: string, name: string) {
    return `u:${this._userId};d:${docId};${name}`;
  }

  private _setDeveloperPromptVersion(developerPromptVersion: DeveloperPromptVersion) {
    const history = this._history.get();
    if (history.developerPromptVersion === developerPromptVersion) { return; }
    if (history.messages.length > 0) { this._assistant.clear(); }
    this._history.set({ ...this._history.get(), developerPromptVersion });
  }

  private async _sendMessage(message: string) {
    const { developerPromptVersion = "default", state } = this._history.get();
    return await askAI(this._gristDoc, {
      description: message,
      conversationId: this._assistant.conversationId,
      developerPromptVersion,
      state,
    });
  }

  private _logTelemetryEvent(
    event: TelemetryEvent,
    metadata: TelemetryMetadata = {},
  ) {
    logTelemetryEvent(event, {
      full: {
        version: 2,
        docIdDigest: this._gristDoc.docId(),
        conversationId: this._assistant.conversationId,
        context: {
          viewId: this._gristDoc.activeViewId.get(),
        },
        ...metadata,
      },
    });
  }
}

export function buildOpenAssistantButton(gristDoc: GristDoc, ...args: DomElementArg[]) {
  const assistant = gristDoc.assistantPanel;
  if (!assistant) { return null; }

  return cssPageEntry(
    cssPageButton(
      cssSparkleDisc(
        cssSparkleDisc.cls("-active", assistant.isOpen),
        cssSparklePageIcon("Sparkle"),
      ),
      cssLinkText(t("Assistant")),
      allCommands.toggleAssistant?.humanKeys.length ?
        cssAssistantShortcut(
          allCommands.toggleAssistant.humanKeys[0],
          testId("tools-shortcut-keys"),
        ) : null,
      dom.cls("tour-assistant"),
      dom.on("click", () => {
        if (assistant.isOpen.get()) {
          assistant.close();
        } else {
          assistant.open();
        }
      }),
      ...args,
    ));
}

async function askAI(
  grist: GristDoc,
  options: {
    description: string;
    conversationId: string;
    developerPromptVersion: DeveloperPromptVersion;
    state?: AssistanceState;
  },
) {
  const { description, conversationId, developerPromptVersion, state } = options;
  const viewId = grist.activeViewId.get();
  return await grist.docComm.getAssistance({
    conversationId,
    context: {
      viewId: typeof viewId === "number" ? viewId : undefined,
    },
    text: description,
    developerPromptVersion,
    state,
  });
}

function buildIntroMessage(...args: DomElementArg[]) {
  return cssAiIntroMessage(
    cssAvatar(cssAiImage()),
    dom("div",
      cssAiMessageParagraph(t("Hi, I'm the Grist AI Assistant.")),
      cssAiMessageParagraph(
        t("Some things you should know when working with me:"),
      ),
      cssAiMessageList(
        cssAiMessageListItem(
          inlineMarkdown(
            t(
              "I **can** answer questions about your data, create or modify " +
              "tables, pages, and most widgets. I can link widgets and help " +
              "with formulas. I also know which page you're viewing.",
            ),
          ),
        ),
        cssAiMessageListItem(
          inlineMarkdown(
            t(
              "I **can't** create or modify charts, forms, access rules, or " +
              "modify page layout. I also don't know if you've selected something.",
            ),
          ),
        ),
        cssAiMessageListItem(
          t(
            'Talk to me like a person. For example, "What were the top ' +
            '3 months for gross revenue in the last year?"',
          ),
        ),
        getGristConfig().assistant?.provider === "OpenAI" ?
          cssAiMessageListItem(
            dom("div",
              t(
                "When you talk to me, your questions, document structure, " +
                "and data are sent to OpenAI. {{learnMore}}",
                {
                  learnMore: cssLink(t("Learn more."), {
                    href: commonUrls.helpAssistantDataUse,
                    target: "_blank",
                  }),
                },
              ),
            ),
          ) :
          null,
      ),
    ),
    ...args,
  );
}

// A Computed<T> backed by JSON in `storage`. The key is reactive — when its
// dependencies change, the value is re-read from the new key. Writes go back to
// the current key. The returned object also exposes `copyTo(targetKey)` which
// copies the current key's stored value to `targetKey` (used to migrate state
// across a fork before the reactive key flips).
function jsonStorageComputed<T>(props: {
  owner: Disposable;
  storage: Storage;
  keyFn: BindableValue<string>;
  defaultValue: () => T;
}): Computed<T> & { copyTo(targetKey: string): void } {
  const { owner, storage, keyFn, defaultValue } = props;

  const safeWrite = (key: string, value: T) => {
    try {
      storage.setItem(key, JSON.stringify(value));
    } catch (err) {
      log.warn("AssistantPanel: failed to save history", err);
    }
  };

  let activeKey = "";
  const trigger = Observable.create(owner, 0);

  const computed = Computed.create<T>(owner, (use) => {
    use(trigger);
    activeKey = useBindable(use, keyFn);
    return safeJsonParse(storage.getItem(activeKey) ?? "", null) ?? defaultValue();
  });

  computed.onWrite((value) => {
    safeWrite(activeKey, value);
    trigger.set(trigger.get() + 1);
  });

  return Object.assign(computed, {
    copyTo(targetKey: string) {
      const raw = storage.getItem(activeKey);
      if (raw !== null) {
        try {
          storage.setItem(targetKey, raw);
        } catch (err) {
          log.warn("AssistantPanel: failed to copy history", err);
        }
      }
    },
  });
}

const cssAssistantPanel = styled("div", `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  min-height: 0;
  overflow: hidden;
`);

const cssPanelHeader = styled("div", `
  height: 49px;
  flex: none;
  display: flex;
  align-items: center;
  width: 100%;
  min-width: 0;
  padding: 0 10px 0 8px;
  column-gap: 4px;
`);

const cssPanelHeaderIcon = styled(icon, `
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  --icon-color: ${theme.controlFg};
`);

const cssPanelHeaderTitle = styled("div", `
  display: flex;
  align-items: center;
  min-width: 0;
  color: ${theme.lightText};
  font-weight: 500;
`);

const cssTitleBox = styled("div", `
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 7px 8px;
`);

const cssTitleLabel = styled("div", `
  min-width: 0;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`);

const cssPanelToolbar = styled("div", `
  display: flex;
  align-items: center;
  column-gap: 6px;
  margin-left: auto;
`);

const cssAssistantShortcut = styled(cssKeyboardShortcut, `
  .${cssTools.className}-collapsed & {
    display: none;
  }
`);

const cssSparkleDisc = styled("div", `
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 24px;
  height: 24px;
  margin-left: -3px;
  margin-right: calc(var(--page-icon-margin, 8px) - 3px);
  border-radius: 50%;

  &-active {
    background-color: ${theme.activePageBg};
    --icon-color: ${theme.activePageFg};
  }
`);

const cssSparklePageIcon = styled(cssPageIcon, `
  width: 18px;
  height: 18px;
  margin-right: 0;
`);

const cssToolbarButton = styled("div", `
  --icon-color: ${theme.controlSecondaryFg};
  border-radius: 3px;
  padding: 3px;
  cursor: pointer;
  user-select: none;

  &:hover, &.weasel-popup-open {
    background-color: ${theme.hover};
  }
`);

const cssAiMessageList = styled("ul", `
  margin-bottom: 8px;
`);

const cssAiMessageListItem = styled("li", `
  margin-bottom: 8px;
`);
